import type { Language } from './types';

const ko = {
  appTitle: 'YouTube 썸네일 / 쇼츠 커버 생성기',
  appSubtitle: '이미지와 텍스트를 올리고 스타일을 고르면 바로 내려받을 수 있습니다. 업로드한 이미지는 기기를 벗어나지 않습니다.',
  toggleLanguage: '언어 전환',

  upload: '이미지',
  uploadTitle: '이미지 업로드',
  uploadHint: '클릭하거나 이미지를 끌어다 놓으세요 · 붙여넣기(Ctrl+V)도 됩니다',
  uploadDrop: '여기에 놓으세요',
  selectFile: '파일 선택',
  replaceImage: '이미지 교체',
  removeImage: '이미지 제거',
  imageTooLarge: '이미지가 너무 큽니다. 25MB 이하 파일을 사용해 주세요.',
  imageInvalid: '이미지 파일만 업로드할 수 있습니다.',
  imageLoadFailed: '이미지를 불러오지 못했습니다.',

  ratio: '비율',
  ratio169: '16:9 · YouTube 썸네일',
  ratio916: '9:16 · Shorts 커버',

  templates: '템플릿',
  presets: '내 프리셋',
  savePreset: '현재 스타일 저장',
  presetNamePrompt: '프리셋 이름',
  applyPreset: '적용',
  deletePreset: '삭제',
  noPresets: '저장된 프리셋이 없습니다. 마음에 드는 스타일을 만든 뒤 저장해 보세요.',
  presetSaved: '프리셋을 저장했습니다.',
  presetApplied: '프리셋을 적용했습니다.',

  text: '텍스트',
  mainTitle: '메인 타이틀',
  subTitle: '서브 타이틀',
  mainTitlePlaceholder: '여기에 제목을 입력하세요',
  subTitlePlaceholder: '부제목 (선택)',
  multilineHint: 'Enter 로 줄바꿈할 수 있습니다',

  fontStyle: '폰트 / 스타일',
  font: '폰트',
  koreanFonts: '한글 폰트',
  englishFonts: '영문 폰트',
  weight: '굵기',
  weightRegular: '보통',
  weightBold: '굵게',
  weightBlack: '아주 굵게',
  size: '크기',
  color: '색상',
  align: '정렬',
  alignLeft: '왼쪽',
  alignCenter: '가운데',
  alignRight: '오른쪽',
  lineHeight: '줄 간격',
  letterSpacing: '자간',

  stroke: '테두리',
  strokeEnabled: '텍스트 테두리',
  thickness: '두께',
  shadow: '그림자',
  shadowEnabled: '그림자',
  softness: '번짐',
  background: '배경',
  backgroundEnabled: '텍스트 배경',
  opacity: '투명도',
  padding: '여백',
  round: '모서리',

  imageFilter: '이미지 필터',
  brightness: '밝기',
  contrast: '대비',
  saturate: '채도',
  blur: '블러',
  overlay: '오버레이',
  overlayColor: '오버레이 색상',
  backgroundColor: '바탕색',

  frame: '썸네일 테두리',
  frameEnabled: '테두리 적용',

  exportSection: '내보내기',
  exportSize: '해상도',
  exportFormat: '포맷',
  exportQuality: '품질',
  download: '다운로드',
  downloading: '만드는 중…',
  copyToClipboard: '클립보드에 복사',
  copied: '클립보드에 복사했습니다.',
  copyFailed: '이 브라우저에서는 클립보드 복사를 지원하지 않습니다.',
  downloaded: '다운로드를 시작했습니다.',
  exportFailed: '이미지를 만들지 못했습니다.',
  needImage: '먼저 이미지를 업로드하세요.',

  preview: '미리보기',
  previewEmpty: '이미지를 업로드하면 이곳에 미리보기가 나타납니다.',
  previewAria: '썸네일 미리보기. 텍스트를 끌어서 옮기거나, 선택한 뒤 방향키로 이동할 수 있습니다.',
  dragHint: '텍스트를 끌어서 위치를 바꾸세요. 선택 후 방향키 1px, Shift+방향키 10px 이동합니다.',
  selectedLayer: '선택됨',
  selectNone: '선택 해제',
  centerLayer: '가운데로',

  undo: '실행취소',
  redo: '다시실행',
  reset: '처음 상태로',
  resetConfirm: '모든 설정을 처음 상태로 되돌릴까요? 업로드한 이미지도 지워집니다.',
  restored: '이전 작업을 복원했습니다.',

  shortcuts: '단축키',
  shortcutUndo: '실행취소',
  shortcutRedo: '다시실행',
  shortcutDownload: '다운로드',
  shortcutMove: '선택한 텍스트 이동',
  shortcutMoveFast: '10px 씩 이동',

  copyright: '© 2025 Thumbnail Generator',
  close: '닫기',
  cancel: '취소',
  save: '저장',
};

export type Dictionary = typeof ko;

const en: Dictionary = {
  appTitle: 'YouTube Thumbnail / Shorts Cover Generator',
  appSubtitle: 'Drop in an image, add your text, pick a style, and download. Your image never leaves your device.',
  toggleLanguage: 'Switch language',

  upload: 'Image',
  uploadTitle: 'Upload an image',
  uploadHint: 'Click or drag an image here · pasting (Ctrl+V) works too',
  uploadDrop: 'Drop it here',
  selectFile: 'Choose file',
  replaceImage: 'Replace image',
  removeImage: 'Remove image',
  imageTooLarge: 'That image is too large. Please use a file under 25 MB.',
  imageInvalid: 'Only image files can be uploaded.',
  imageLoadFailed: 'The image could not be loaded.',

  ratio: 'Ratio',
  ratio169: '16:9 · YouTube thumbnail',
  ratio916: '9:16 · Shorts cover',

  templates: 'Templates',
  presets: 'My presets',
  savePreset: 'Save current style',
  presetNamePrompt: 'Preset name',
  applyPreset: 'Apply',
  deletePreset: 'Delete',
  noPresets: 'No presets yet. Build a look you like, then save it here.',
  presetSaved: 'Preset saved.',
  presetApplied: 'Preset applied.',

  text: 'Text',
  mainTitle: 'Main title',
  subTitle: 'Sub title',
  mainTitlePlaceholder: 'Type your headline here',
  subTitlePlaceholder: 'Subtitle (optional)',
  multilineHint: 'Press Enter for a line break',

  fontStyle: 'Font / style',
  font: 'Font',
  koreanFonts: 'Korean fonts',
  englishFonts: 'Latin fonts',
  weight: 'Weight',
  weightRegular: 'Regular',
  weightBold: 'Bold',
  weightBlack: 'Black',
  size: 'Size',
  color: 'Colour',
  align: 'Align',
  alignLeft: 'Left',
  alignCenter: 'Centre',
  alignRight: 'Right',
  lineHeight: 'Line height',
  letterSpacing: 'Letter spacing',

  stroke: 'Outline',
  strokeEnabled: 'Text outline',
  thickness: 'Thickness',
  shadow: 'Shadow',
  shadowEnabled: 'Shadow',
  softness: 'Softness',
  background: 'Background',
  backgroundEnabled: 'Text background',
  opacity: 'Opacity',
  padding: 'Padding',
  round: 'Corner',

  imageFilter: 'Image filters',
  brightness: 'Brightness',
  contrast: 'Contrast',
  saturate: 'Saturation',
  blur: 'Blur',
  overlay: 'Overlay',
  overlayColor: 'Overlay colour',
  backgroundColor: 'Canvas colour',

  frame: 'Thumbnail border',
  frameEnabled: 'Show border',

  exportSection: 'Export',
  exportSize: 'Resolution',
  exportFormat: 'Format',
  exportQuality: 'Quality',
  download: 'Download',
  downloading: 'Rendering…',
  copyToClipboard: 'Copy to clipboard',
  copied: 'Copied to clipboard.',
  copyFailed: 'This browser cannot copy images to the clipboard.',
  downloaded: 'Download started.',
  exportFailed: 'The image could not be generated.',
  needImage: 'Upload an image first.',

  preview: 'Preview',
  previewEmpty: 'Upload an image and your preview will appear here.',
  previewAria: 'Thumbnail preview. Drag the text to move it, or select it and use the arrow keys.',
  dragHint: 'Drag the text to reposition it. Arrow keys move 1 px, Shift + arrows move 10 px.',
  selectedLayer: 'Selected',
  selectNone: 'Deselect',
  centerLayer: 'Centre',

  undo: 'Undo',
  redo: 'Redo',
  reset: 'Reset',
  resetConfirm: 'Reset everything to defaults? The uploaded image will be removed too.',
  restored: 'Restored your previous session.',

  shortcuts: 'Shortcuts',
  shortcutUndo: 'Undo',
  shortcutRedo: 'Redo',
  shortcutDownload: 'Download',
  shortcutMove: 'Move selected text',
  shortcutMoveFast: 'Move by 10 px',

  copyright: '© 2025 Thumbnail Generator',
  close: 'Close',
  cancel: 'Cancel',
  save: 'Save',
};

export const dictionaries: Record<Language, Dictionary> = { ko, en };

export function detectLanguage(): Language {
  if (typeof navigator === 'undefined') return 'en';
  return navigator.language?.toLowerCase().startsWith('ko') ? 'ko' : 'en';
}
